import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";

import {
  getSVGBase64DataURL,
  HitTest,
  IBrushManagerOptions,
  INode,
  ISingleNode,
  isUndefined,
} from "@noli/core";
import { NodeExporter } from "@noli/svg";
import { ABCStore, BoardStore, flagsStore, toolbarStore } from "@noli/business";

import { Txt2ImgStyles, Txt2ImgArtists, Txt2ImgCats, defaultBrushColor } from "@/utils/constants";
import {
  allSemanticColorStrings,
  defaultSemanticColorString,
  defaultSemanticType,
} from "@/utils/constants";
import { getPreviewSrc, toFixed } from "@/utils/misc";
import { PromptInfo } from "@/types/meta";
import {
  defaultOptimizers,
  ModelTypes,
  ModeTypes,
  SDSampler,
  allSDSamplers,
  MODEL_TYPES_INFO_WITH_THIRD_PARTY,
  ModelTypesWithMultiThirdParty,
  ModelTypesWithThirdParty,
  NoliBoxModelInfo,
  DEFAULT_NUM_STEPS,
  DEFAULT_GUIDANCE_SCALE,
  modelDefaultOptimizers,
} from "@/modules/editor/constant";
import { OptimizerType } from "@/types/creator";
import { WenxinStyles } from "@/thirdParty/wenxin/types";
import {
  isThirdParty,
  MultiModelInfo,
  ThirdParty,
  ThirdPartyModelTypes,
} from "@/thirdParty/constants";
import { isNoliboxModel } from "@/modules/editor/components/common/ModelSwitch/util";

export type Txt2ImgSizes = "1:1" | "3:4" | "4:3";

export interface IStatusStore {
  inpaintingImageAlias?: string;
  inpaintingMaskAlias?: string;
  outpaintingAreaAlias?: string;
  outpaintingSrc?: string;
  pictureMergingBGAlias?: string;
  pictureMergingFGAlias?: string;
  inNormalBrushMode?: boolean;
  landscapeType?: string;
  txt2imgTexts: string;
  txt2imgJson: string;
  txt2imgStyle: Txt2ImgStyles;
  txt2imgArtist: Txt2ImgArtists;
  txt2imgSize: Txt2ImgSizes;
  txt2imgCat: Txt2ImgCats;
  txt2imgCatTexts: string;
  negativePrompt: string;
  optimizers: OptimizerType[];
  dropping: boolean;
  wenxinStyle: WenxinStyles;
  modelType: ModelTypes;
  mode: ModeTypes;
  advancePrompt?: string;
  num_steps?: number;
  guidance_scale?: number;
  sampler?: SDSampler;
  seed?: number;
  useNegativePrompt: boolean;
  promptSettings?: PromptInfo["promptSettings"];
  negativePromptSettings?: PromptInfo["negativePromptSettings"];
  advanceModeNegativePrompt: string;
  txt2imgCustomSize?: { w: number; h: number };
  generateCount: number;
  thirdPartyModelInfo: Record<ModelTypesWithThirdParty, NoliBoxModelInfo | MultiModelInfo>;
  thirdPartyModelSelectInfo: {
    // 选择模型开关状态
    open?: boolean;
    // 模型名称
    model?: string;
  };
  smallModels: string[];
  animeSmallModels: string[];
  promptExpanding: boolean;
}
class StatusStore extends ABCStore<IStatusStore> implements IStatusStore {
  inpaintingImageAlias?: string = undefined;
  inpaintingMaskAlias?: string = undefined;
  outpaintingAreaAlias?: string = undefined;
  outpaintingSrc?: string = undefined;
  pictureMergingBGAlias?: string = undefined;
  pictureMergingFGAlias?: string = undefined;
  inNormalBrushMode = false;
  landscapeType?: string = undefined;
  txt2imgTexts = "";
  txt2imgJson = "";
  txt2imgStyle: Txt2ImgStyles = "none-style";
  txt2imgArtist: Txt2ImgArtists = "none-artist";
  txt2imgSize: Txt2ImgSizes = "1:1";
  txt2imgCat: Txt2ImgCats = "none-cat";
  txt2imgCatTexts = "";
  negativePrompt = "";
  modelType: ModelTypes = "deliberate-general";
  optimizers =
    (isNoliboxModel(this.modelType) && modelDefaultOptimizers[this.modelType]) || defaultOptimizers;
  dropping = false;
  wenxinStyle: WenxinStyles = "水彩画";
  mode: ModeTypes = "simple";
  advancePrompt = "";
  useNegativePrompt = false;
  advanceModeNegativePrompt = "";
  num_steps = DEFAULT_NUM_STEPS;
  guidance_scale = DEFAULT_GUIDANCE_SCALE;
  sampler = allSDSamplers[0];
  seed = -1;
  promptSettings?: PromptInfo["promptSettings"] = [];
  negativePromptSettings?: PromptInfo["negativePromptSettings"] = [];
  txt2imgCustomSize: { w: number; h: number } = { w: 512, h: 512 };
  generateCount = 1;
  thirdPartyModelInfo = MODEL_TYPES_INFO_WITH_THIRD_PARTY;
  thirdPartyModelSelectInfo = {
    open: false,
    model: "",
  };
  smallModels: string[] = [];
  animeSmallModels: string[] = [];
  promptExpanding = false;

  constructor() {
    super();
    makeObservable(this, {
      inpaintingImageAlias: observable,
      inpaintingMaskAlias: observable,
      inpaintingImageSrc: computed,
      inpaintingMaskSrc: computed,
      inpaintingReady: computed,
      outpaintingAreaAlias: observable,
      outpaintingSrc: observable,
      outpaintingOverlapped: computed,
      outpaintingReady: computed,
      pictureMergingBGAlias: observable,
      pictureMergingFGAlias: observable,
      pictureMergingFGSrc: computed,
      pictureMergingBGSrc: computed,
      pictureMergingReady: computed,
      inNormalBrushMode: observable,
      landscapeType: observable,
      inLandscapeMode: computed,
      switchNormalBrushMode: action,
      switchLandscapeMode: action,
      switchLandscapeType: action,
      txt2imgTexts: observable,
      txt2imgJson: observable,
      txt2imgStyle: observable,
      txt2imgArtist: observable,
      txt2imgSize: observable,
      txt2imgCat: observable,
      txt2imgCatTexts: observable,
      negativePrompt: observable,
      dropping: observable,
      wenxinStyle: observable,
      modelType: observable,
      mode: observable,
      useNegativePrompt: observable,
      promptSettings: observable,
      negativePromptSettings: observable,
      advanceModeNegativePrompt: observable,
      txt2imgCustomSize: observable,
      optimizers: observable,
      advancePrompt: observable,
      num_steps: observable,
      guidance_scale: observable,
      sampler: observable,
      seed: observable,
      generateCount: observable,
      thirdPartyModelInfo: observable,
      thirdPartyModelSelectInfo: observable,
      smallModels: observable,
      animeSmallModels: observable,
      promptExpanding: observable,
    });
  }

  setSelectedThirdParty(model: ThirdPartyModelTypes | ThirdParty) {
    this.thirdPartyModelInfo["third-party"].value = model;
  }
  setThirdPartyModelSelectInfo(info?: IStatusStore["thirdPartyModelSelectInfo"]) {
    if (!info) {
      this.thirdPartyModelSelectInfo = { open: false, model: "" };
    } else {
      this.thirdPartyModelSelectInfo = {
        ...this.thirdPartyModelSelectInfo,
        ...info,
      };
    }
  }

  get info(): IStatusStore {
    return this;
  }

  get inpaintingImageSrc(): string | undefined {
    return getPreviewSrc(this.inpaintingImageAlias);
  }
  get inpaintingMaskSrc(): string | undefined {
    return getPreviewSrc(this.inpaintingMaskAlias);
  }
  get inpaintingReady(): boolean {
    console.log("this.inpaintingImageAlias: ", this.inpaintingImageAlias);
    console.log("this.inpaintingMaskAlias: ", this.inpaintingMaskAlias);
    if (isUndefined(this.inpaintingImageAlias) || isUndefined(this.inpaintingMaskAlias)) {
      return false;
    }
    if (!BoardStore.graph.has(this.inpaintingImageAlias)) {
      return false;
    }
    if (!BoardStore.graph.has(this.inpaintingMaskAlias)) {
      return false;
    }
    return true;
  }
  get outpaintingOverlapped(): ISingleNode[] | undefined {
    const alias = this.outpaintingAreaAlias;
    flagsStore.latestRecordAliases.size > 0;
    if (isUndefined(alias)) return undefined;
    const graph = BoardStore.graph.snapshot();
    const areaNode = graph.getNode<ISingleNode>(alias);
    if (!areaNode) return undefined;
    const areaBBox = areaNode.bbox;
    return graph.allSingleNodes.filter(
      (node) => node.alias !== alias && HitTest.test(areaBBox, node.bbox),
    );
  }

  get outpaintingReady(): boolean {
    if (isUndefined(this.outpaintingAreaAlias)) {
      return false;
    }
    if (!BoardStore.graph.has(this.outpaintingAreaAlias)) {
      return false;
    }
    return true;
  }
  get pictureMergingBGSrc(): string | undefined {
    return getPreviewSrc(this.pictureMergingBGAlias);
  }
  get pictureMergingFGSrc(): string | undefined {
    return getPreviewSrc(this.pictureMergingFGAlias);
  }
  get pictureMergingReady(): boolean {
    if (isUndefined(this.pictureMergingBGAlias) || isUndefined(this.pictureMergingFGAlias)) {
      return false;
    }
    if (!BoardStore.graph.has(this.pictureMergingBGAlias)) {
      return false;
    }
    if (!BoardStore.graph.has(this.pictureMergingFGAlias)) {
      return false;
    }
    return true;
  }

  setThirdPartyModelInfo(thirdModelType: ThirdParty | ThirdPartyModelTypes) {
    this.thirdPartyModelInfo["third-party"].value = thirdModelType;
  }

  switchNormalBrushMode(): void {
    const opt = {
      stroke: defaultBrushColor,
      fill: defaultBrushColor,
      width: 1,
      useMultiple: false,
    };
    toolbarStore.switchBrushMode(opt);
    this.inNormalBrushMode = toolbarStore.inBrushMode;
  }
  get inLandscapeMode(): boolean {
    return !!this.landscapeType;
  }
  switchLandscapeMode(): void {
    let opt: IBrushManagerOptions | undefined;
    if (toolbarStore.inBrushMode) {
      this.landscapeType = undefined;
    } else {
      this.landscapeType = defaultSemanticType;
      opt = {
        useMultiple: true,
        aliasPrefix: "landscape",
        stroke: defaultSemanticColorString,
        fill: defaultSemanticColorString,
      };
    }
    toolbarStore.switchBrushMode(opt);
  }
  switchLandscapeType(type: string): void {
    this.landscapeType = type;
    const colorString = allSemanticColorStrings[type];
    toolbarStore.updateBrushOptions({ stroke: colorString, fill: colorString });
  }

  getTxt2imgGenerateSize(displayNode?: INode | null): { w: number; h: number } {
    if (displayNode?.type === "rectangle") {
      const { w, h } = displayNode.wh;
      return { w: toFixed(w, 2), h: toFixed(Math.abs(h), 2) };
    }
    switch (statusStore.txt2imgSize) {
      case "3:4":
        return { w: 512, h: 704 };
      case "4:3":
        return { w: 704, h: 512 };
      case "1:1":
      default:
        return { w: 512, h: 512 };
    }
  }
}

export const statusStore = new StatusStore();

reaction(
  () => {
    const alias = statusStore.outpaintingAreaAlias;
    const overlapped = statusStore.outpaintingOverlapped;
    return { alias, overlapped };
  },
  ({ alias, overlapped }) => {
    if (isUndefined(alias) || isUndefined(overlapped)) {
      statusStore.updateProperty("outpaintingSrc", undefined);
      return;
    }

    const areaNode = BoardStore.graph.getNode<ISingleNode>(alias)?.snapshot();
    if (!areaNode) {
      runInAction(() => {
        statusStore.outpaintingAreaAlias = undefined;
        statusStore.outpaintingSrc = undefined;
      });
      return;
    }
    const bounding = areaNode.bbox.bounding.toAABB();
    new NodeExporter()
      .export(overlapped.concat([areaNode]), { exportBox: bounding })
      .then(({ svg }) => {
        statusStore.updateProperty("outpaintingSrc", getSVGBase64DataURL(svg.svg()));
      });
  },
);

export const updateStatus = statusStore.updateProperty.bind(statusStore);
export const switchNormalBrushMode = statusStore.switchNormalBrushMode.bind(statusStore);
export const switchLandscapeMode = statusStore.switchLandscapeMode.bind(statusStore);
export const switchLandscapeType = statusStore.switchLandscapeType.bind(statusStore);
export const setTxt2ImgTexts = (text: string) => statusStore.updateProperty("txt2imgTexts", text);
export const setTxt2ImgJson = (text: string) => statusStore.updateProperty("txt2imgJson", text);
export const setTxt2ImgStyle = (style: Txt2ImgStyles) =>
  statusStore.updateProperty("txt2imgStyle", style);
export const setTxt2ImgArtist = (artist: Txt2ImgArtists) =>
  statusStore.updateProperty("txt2imgArtist", artist);
export const setTxt2ImgSize = (size: Txt2ImgSizes) =>
  statusStore.updateProperty("txt2imgSize", size);
export const setTxt2ImgCat = (cat: Txt2ImgCats) => statusStore.updateProperty("txt2imgCat", cat);
export const setTxt2ImgCatTexts = (text: string) =>
  statusStore.updateProperty("txt2imgCatTexts", text);
export const setNegativePrompt = (text: string) =>
  statusStore.updateProperty("negativePrompt", text);
export const setUseNegativePrompt = (open: boolean) =>
  statusStore.updateProperty("useNegativePrompt", open);
export const setOptimizers = (optimizers: OptimizerType[]) =>
  statusStore.updateProperty("optimizers", optimizers);
export const setDropping = (dropping: boolean) => statusStore.updateProperty("dropping", dropping);
export const setWenxinStyle = (style: WenxinStyles) =>
  statusStore.updateProperty("wenxinStyle", style);
export const setModelType = (modelType: ModelTypesWithMultiThirdParty) => {
  if (isThirdParty(modelType)) {
    statusStore.updateProperty("thirdPartyModelSelectInfo", { open: true });
    if (modelType !== "third-party") statusStore.updateProperty("modelType", modelType);
  } else {
    statusStore.setSelectedThirdParty("third-party");
    statusStore.updateProperty("modelType", modelType);
  }
};

export const setPromptExpanding = (waiting: boolean) => {
  statusStore.updateProperty("promptExpanding", waiting);
};

export const setThirdPartyModel = (model: string) => {
  statusStore.setSelectedThirdParty(model as ThirdPartyModelTypes | "third-party");
  setModelType(model as ThirdPartyModelTypes | "third-party");
};
export const setSelectedThirdPartyModel = (name: string) => {
  statusStore.setThirdPartyModelSelectInfo({ model: name });
};

export const openThirdPartyModelSelect = () => {
  statusStore.setThirdPartyModelSelectInfo({ open: true });
};
export const closeThirdPartyModelSelect = () => {
  statusStore.setThirdPartyModelSelectInfo({ open: false });
};
export const setMode = (mode: ModeTypes) => statusStore.updateProperty("mode", mode);

export const setAdvancePrompt = (text: string) => {
  statusStore.updateProperty("advancePrompt", text);
};
export const setPromptSettings = (promptSettings?: PromptInfo["promptSettings"]) =>
  statusStore.updateProperty("promptSettings", promptSettings);
export const setNegativePromptSettings = (
  negativePromptSettings?: PromptInfo["negativePromptSettings"],
) => statusStore.updateProperty("negativePromptSettings", negativePromptSettings);
export const updatePromptSettings = (record: string) => {
  if (!statusStore.promptSettings) {
    setPromptSettings([record]);
    return;
  }
  const specificWord = record
    .split("|")
    .map((str) => str.trim())
    .pop();

  const existedWordIndex = statusStore.promptSettings.findIndex((keys) => {
    return (
      keys
        .split("|")
        .map((str) => str.trim())
        .pop() === specificWord
    );
  });

  if (existedWordIndex > -1) {
    const newList = statusStore.promptSettings.concat();
    newList.splice(existedWordIndex, 1);
    setPromptSettings(newList);
  } else {
    setPromptSettings(statusStore.promptSettings.concat(record));
  }
};
export const setAdvanceModeNegativePrompt = (text: string) => {
  statusStore.updateProperty("advanceModeNegativePrompt", text);
};
export const updateNegativePromptSettings = (record: string) => {
  if (!statusStore.negativePromptSettings) {
    setNegativePromptSettings([record]);
    return;
  }
  const specificWord = record
    .split("|")
    .map((str) => str.trim())
    .pop();

  const existedWordIndex = statusStore.negativePromptSettings.findIndex((keys) => {
    return (
      keys
        .split("|")
        .map((str) => str.trim())
        .pop() === specificWord
    );
  });

  if (existedWordIndex > -1) {
    const newList = statusStore.negativePromptSettings.concat();
    newList.splice(existedWordIndex, 1);
    setNegativePromptSettings(newList);
  } else {
    setNegativePromptSettings(statusStore.negativePromptSettings.concat(record));
  }
};

export const updateTxt2imgCustomSize = (key: string, value: number) =>
  statusStore.updateProperty("txt2imgCustomSize", {
    ...statusStore.txt2imgCustomSize,
    [key]: value,
  });
export const setTxt2imgCustomSize = (values: IStatusStore["txt2imgCustomSize"]) =>
  statusStore.updateProperty("txt2imgCustomSize", values);

export const setGenerateCount = (count: number) =>
  statusStore.updateProperty("generateCount", count);

export const setGuidanceScale = (guidanceScale: number) =>
  statusStore.updateProperty("guidance_scale", guidanceScale);

export const setNumSteps = (num_steps: number) =>
  statusStore.updateProperty("num_steps", num_steps);

export const setSampler = (sampler: SDSampler) => statusStore.updateProperty("sampler", sampler);
export const setSeed = (seed: number) => statusStore.updateProperty("seed", seed);

export const updateSmallModels = (smallModel: string) => {
  const newModels = statusStore.smallModels.concat();

  if (statusStore.smallModels.includes(smallModel)) {
    newModels.splice(statusStore.smallModels.indexOf(smallModel), 1);
    statusStore.updateProperty("smallModels", newModels);
  } else {
    newModels.push(smallModel);
    statusStore.updateProperty("smallModels", newModels);
  }
};

export const updateAnimeSmallModels = (smallModel: string) => {
  const newModels = statusStore.animeSmallModels.concat();

  if (statusStore.animeSmallModels.includes(smallModel)) {
    newModels.splice(statusStore.animeSmallModels.indexOf(smallModel), 1);
    statusStore.updateProperty("animeSmallModels", newModels);
  } else {
    newModels.push(smallModel);
    statusStore.updateProperty("animeSmallModels", newModels);
  }
};
